import { type FC, type FormEvent, useCallback, useEffect } from "react";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Button } from "@/components/flexkit/Button";
import { GCPLogo } from "@/assets/logos/GCPLogo";
import { Text } from "@/components/podkit/typography/Text";
import { useRunnerName } from "@/hooks/use-runner-name";

export const NewGCPRunnerForm: FC<
    {
        formId: string;
        onSubmit: (values: { name: string }) => Promise<void>;
        onDisabledChange: (disabled: boolean) => void;
    } & PropsWithClassName
> = ({ formId, onDisabledChange, className, onSubmit }) => {
    const { name, setName, isLoading } = useRunnerName({
        baseName: "GCP",
        provider: RunnerProvider.GCP,
    });

    // Update disabled state when name changes
    useEffect(() => {
        onDisabledChange(!name);
    }, [name, onDisabledChange]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!name) {
                return;
            }
            await onSubmit({ name });
        },
        [name, onSubmit],
    );

    if (isLoading) {
        return <></>;
    }

    return (
        <form name={formId} id={formId} onSubmit={handleSubmit}>
            <div className={cn("flex flex-col gap-4", className)}>
                {/* Header Section */}
                <div className="flex items-start gap-4 rounded-lg bg-surface-secondary p-4">
                    <div className="flex-shrink-0">
                        <GCPLogo className="size-12" />
                    </div>
                    <div className="flex grow flex-col gap-3">
                        <div>
                            <Text className="text-base font-semibold">GCP Runner</Text>
                            <Text className="text-sm text-content-secondary">
                                Deploy a runner on Google Cloud Platform using Terraform for scalable environment
                                execution.
                            </Text>
                        </div>
                        <div className="space-y-2">
                            <Text className="text-sm">Best suited for:</Text>
                            <ul className="list-disc space-y-1 pl-4 text-sm text-content-secondary">
                                <li>Teams using Google Cloud Platform</li>
                                <li>Infrastructure as Code with Terraform</li>
                                <li>Scalable production workloads</li>
                                <li>Integration with GCP services</li>
                            </ul>
                        </div>
                        <div>
                            <Button variant="secondary" size="sm" asChild>
                                <ExternalLink href="https://github.com/gitpod-io/gitpod-gcp-terraform" iconSize="sm">
                                    <span>View Terraform Repository</span>
                                </ExternalLink>
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Runner Name Input */}
                <div className="space-y-1">
                    <Label htmlFor="name">Name</Label>
                    <Input
                        id="name"
                        type="text"
                        name="runner_name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="max-w-full"
                        placeholder="e.g., gcp-production-runner"
                    />
                </div>
            </div>
        </form>
    );
};
